import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Bot, Cpu, Shield, Search, Megaphone, BookOpen, GitBranch, Server, Zap, BrainCircuit, Activity, Scale, ChevronDown, Save, Settings } from 'lucide-react';
import { StatusDot } from '@/components/shared/StatusDot';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ICONS = { 'MOA': BrainCircuit, 'ARIA': Zap, 'CHANGE-IQ': GitBranch, 'ATLAS': Server, 'SENTINL': Shield, 'NOVA': Search, 'CONCIERGE': Bot, 'CAPACITOR': Cpu, 'HERALD': Megaphone, 'LEARN-X': BookOpen, 'COMPLIANCE-OPS': Shield };
const COLORS = { 'MOA': '#3B82F6', 'ARIA': '#EF4444', 'CHANGE-IQ': '#F59E0B', 'ATLAS': '#06B6D4', 'SENTINL': '#EF4444', 'NOVA': '#8B5CF6', 'CONCIERGE': '#10B981', 'CAPACITOR': '#F97316', 'HERALD': '#EC4899', 'LEARN-X': '#6366F1', 'COMPLIANCE-OPS': '#F59E0B' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="p-2 rounded-lg bg-[var(--nexus-bg-surface)] border border-[var(--nexus-border)] shadow-lg">
      <p className="font-mono text-[10px] text-[var(--nexus-text-muted)] mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }}></span>
          <span className="text-[10px] text-[var(--nexus-text-secondary)]">{p.name}: {p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function AgentScalingPage() {
  const token = localStorage.getItem('nexus_token');
  const queryClient = useQueryClient();
  const [editingAgent, setEditingAgent] = useState(null);
  const [editLimits, setEditLimits] = useState({});
  const [editModel, setEditModel] = useState({});
  const [showModelPanel, setShowModelPanel] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const { data: scaling } = useQuery({ queryKey: ['agent-scaling'], queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/agents/scaling`, { headers }); return data; }, refetchInterval: 5000 });
  const { data: history } = useQuery({ queryKey: ['scaling-history'], queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/agents/scaling/history`, { headers }); return data; } });
  const { data: models } = useQuery({ queryKey: ['agent-models'], queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/agents/models`, { headers }); return data; } });
  const { data: busStats } = useQuery({ queryKey: ['message-bus-stats'], queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/agents/messages/stats`, { headers }); return data; }, refetchInterval: 10000 });

  const scaleMutation = useMutation({
    mutationFn: async ({ name, limits }) => { const { data } = await axios.patch(`${BACKEND_URL}/api/agents/scaling/${name}`, limits, { headers }); return data; },
    onSuccess: () => { toast.success('Scaling limits updated'); setEditingAgent(null); queryClient.invalidateQueries(['agent-scaling']); },
  });

  const modelMutation = useMutation({
    mutationFn: async ({ name, model }) => { const { data } = await axios.patch(`${BACKEND_URL}/api/agents/models/${name}`, model, { headers }); return data; },
    onSuccess: (data) => { toast.success(`${data.agent} model updated`); setShowModelPanel(null); queryClient.invalidateQueries(['agent-models']); },
  });

  const chartData = scaling ? Object.entries(scaling).map(([name, s]) => ({ name, workers: s.active_workers, queue: s.queue_depth, utilization: s.utilization })) : [];

  return (
    <div data-testid="agent-scaling-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Agent Scaling & Orchestration</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Worker pools, auto-scaling, model selection, inter-agent communication</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Total Workers" value={scaling ? Object.values(scaling).reduce((s, a) => s + a.active_workers, 0) : 0} color="var(--nexus-accent)" />
        <StatCard label="Total Queue" value={scaling ? Object.values(scaling).reduce((s, a) => s + a.queue_depth, 0) : 0} color="var(--nexus-warning)" />
        <StatCard label="Messages Processed" value={busStats?.delivered || 0} color="var(--nexus-success)" />
        <StatCard label="Scaling Events" value={(history || []).length} color="var(--nexus-info)" />
      </div>

      {/* Worker chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Worker Distribution</h2>
          </div>
          <div className="p-3">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
                <XAxis dataKey="name" tick={{ fontSize: 8, fill: '#94A3B8' }} axisLine={{ stroke: '#1E2A45' }} />
                <YAxis tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#1E2A45' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="workers" name="Workers" fill="#06B6D4" radius={[4,4,0,0]} />
                <Bar dataKey="queue" name="Queue" fill="#F59E0B" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Message Bus Stats */}
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <div className="flex items-center gap-2">
              <Activity size={14} className="text-[var(--nexus-accent)]" />
              <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Agent Message Bus</h2>
            </div>
          </div>
          <div className="p-4 grid grid-cols-2 gap-3">
            {busStats && Object.entries(busStats).map(([key, val]) => (
              <div key={key} className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                <p className="text-[10px] text-[var(--nexus-text-muted)] uppercase tracking-wider">{key}</p>
                <p className="font-mono text-lg font-semibold text-[var(--nexus-text-primary)] tabular-nums">{val}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Agent worker grid */}
      <div className="nexus-card relative overflow-hidden mb-4">
        <div className="p-3 border-b border-[var(--nexus-border)]">
          <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Agent Workers & Model Configuration</h2>
        </div>
        <div className="p-3 space-y-2" data-testid="agent-workers-grid">
          {scaling && Object.entries(scaling).map(([name, status]) => {
            const Icon = ICONS[name] || Bot;
            const color = COLORS[name] || '#06B6D4';
            const isEditing = editingAgent === name;
            const isModelOpen = showModelPanel === name;
            const defaultModel = models?.defaults?.[name] || {};
            const utilColor = status.utilization > 80 ? 'var(--nexus-danger)' : status.utilization > 50 ? 'var(--nexus-warning)' : 'var(--nexus-success)';

            return (
              <div key={name} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15`, color }}><Icon size={14} /></div>
                
                <div className="w-24 flex-shrink-0">
                  <span className="font-mono text-xs font-bold text-[var(--nexus-text-primary)]">{name}</span>
                </div>

                {/* Workers */}
                <div className="flex items-center gap-1.5 w-28 flex-shrink-0">
                  {[...Array(status.active_workers)].map((_, i) => (
                    <div key={i} className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color, opacity: i < status.queue_depth ? 1 : 0.4 }} />
                  ))}
                  <span className="font-mono text-[10px] text-[var(--nexus-text-muted)] ml-1">{status.active_workers}w</span>
                </div>

                {/* Queue */}
                <div className="w-16 flex-shrink-0">
                  <span className="font-mono text-[10px] text-[var(--nexus-text-muted)]">Q: </span>
                  <span className={`font-mono text-[10px] tabular-nums ${status.queue_depth > 3 ? 'text-[var(--nexus-warning)]' : 'text-[var(--nexus-text-secondary)]'}`}>{status.queue_depth}</span>
                </div>

                {/* Utilization bar */}
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-[var(--nexus-bg-elevated)] overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${status.utilization}%`, backgroundColor: utilColor }} />
                  </div>
                  <span className="font-mono text-[10px] tabular-nums w-8 text-right" style={{ color: utilColor }}>{status.utilization}%</span>
                </div>

                {/* Model badge */}
                <button onClick={() => setShowModelPanel(isModelOpen ? null : name)} className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[var(--nexus-text-muted)] hover:text-[var(--nexus-accent)] transition-colors flex-shrink-0" data-testid={`model-${name}`}>
                  {defaultModel.model?.split('-').slice(0,2).join('-') || 'claude'}
                </button>

                {/* Scale controls */}
                {isEditing ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <input type="number" min="1" max="10" value={editLimits.min_workers || 1} onChange={e => setEditLimits(v => ({...v, min_workers: parseInt(e.target.value)}))} className="w-10 px-1 py-0.5 rounded bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[10px] text-center font-mono text-[var(--nexus-text-primary)]" />
                    <span className="text-[10px] text-[var(--nexus-text-muted)]">-</span>
                    <input type="number" min="1" max="20" value={editLimits.max_workers || 5} onChange={e => setEditLimits(v => ({...v, max_workers: parseInt(e.target.value)}))} className="w-10 px-1 py-0.5 rounded bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[10px] text-center font-mono text-[var(--nexus-text-primary)]" />
                    <button onClick={() => scaleMutation.mutate({ name, limits: editLimits })} className="p-1 text-[var(--nexus-success)]"><Save size={12} /></button>
                  </div>
                ) : (
                  <button onClick={() => { setEditingAgent(name); setEditLimits({ min_workers: 1, max_workers: status.max_workers }); }} className="p-1 text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)] flex-shrink-0" data-testid={`scale-${name}`}>
                    <Scale size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Model selection panel */}
      {showModelPanel && models?.available && (
        <div className="nexus-card relative overflow-hidden mb-4 animate-nexus-slide-in">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Select Model for {showModelPanel}</h2>
              <button onClick={() => setShowModelPanel(null)} className="text-[10px] text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)]">Close</button>
            </div>
          </div>
          <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="model-selector">
            {Object.entries(models.available).map(([provider, providerModels]) => (
              Object.entries(providerModels).map(([modelId, info]) => (
                <button key={modelId} onClick={() => modelMutation.mutate({ name: showModelPanel, model: { provider, model: modelId } })}
                  className="p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:border-[var(--nexus-accent)] text-left transition-colors"
                  data-testid={`select-${modelId}`}>
                  <span className="text-xs font-semibold text-[var(--nexus-text-primary)]">{info.name}</span>
                  <p className="text-[10px] text-[var(--nexus-text-muted)] mt-0.5">{provider} \u2022 {info.tier}</p>
                  <span className="font-mono text-[9px] text-[var(--nexus-accent)]">{(info.tokens_per_min / 1000).toFixed(0)}k tok/min</span>
                </button>
              ))
            ))}
          </div>
        </div>
      )}

      {/* Scaling history */}
      <div className="nexus-card relative overflow-hidden">
        <div className="p-3 border-b border-[var(--nexus-border)]">
          <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Scaling History</h2>
        </div>
        <ScrollArea className="h-[200px]">
          <div className="p-2 space-y-1" data-testid="scaling-history">
            {(history || []).map((event, i) => (
              <div key={event.id || i} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                <StatusDot status={event.action === 'scale_up' ? 'warning' : 'success'} size="sm" />
                <span className="font-mono text-[10px] text-[var(--nexus-accent)]">{event.agent_name}</span>
                <span className="text-[10px] text-[var(--nexus-text-secondary)]">{event.action === 'scale_up' ? '\u2191' : '\u2193'} {event.from_workers}\u2192{event.to_workers} workers</span>
                <span className="text-[10px] text-[var(--nexus-text-muted)]">Queue: {event.queue_depth}</span>
                <span className="font-mono text-[9px] text-[var(--nexus-text-muted)] ml-auto">{event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}</span>
              </div>
            ))}
            {(!history || history.length === 0) && <div className="text-center py-6 text-[var(--nexus-text-muted)] text-xs">No scaling events yet. Trigger scenarios to generate workload.</div>}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, color }) => (
  <div className="nexus-card relative overflow-hidden p-3">
    <p className="text-[10px] text-[var(--nexus-text-muted)] uppercase tracking-wider">{label}</p>
    <p className="font-heading text-2xl font-semibold tabular-nums" style={{ color }}>{value}</p>
  </div>
);
